// List-row rail: checkbox or bulk-select, plus optional focus button.
import { Box, Checkbox } from '@mui/material';
import TaskCheckbox from '@/features/tasks/components/atoms/TaskCheckbox';
import { TaskFocusButton } from '@/features/tasks/components/atoms/TaskExecutionActions';
import { ONBOARDING_HINT_FOCUS } from '@/constants/onboardingHints';
import { useOnboardingHintQueueContext } from '@/features/onboarding/context/useOnboardingHintQueueContext';
import { useTaskUI } from '@/features/tasks/context/TaskUIContext';

/**
 * @param {{
 *   task: Task,
 *   isMultiSelectActive: boolean,
 *   selectedTaskIds: string[],
 *   onToggleInSelection: (id: string) => void,
 *   localCompleted: boolean,
 *   setLocalCompleted: (v: boolean) => void,
 *   user: any,
 *   handleToggleComplete: (task: Task, completed: boolean, context: any) => Promise<void>,
 *   activeFocusTaskId: string|null,
 *   focusStartTime: number|null,
 *   stopFocus: () => void,
 *   isSmallScreen: boolean,
 *   isExecutionView: boolean,
 *   hideCheckboxFromTabOrder?: boolean,
 *   isTouchDevice: boolean,
 *   isFocused: boolean,
 *   showPlayButton: boolean,
 *   handleStartFocus: (id: string) => void,
 *   stopAllPropagation: (e: Event) => void,
 * }} props
 */
export function TaskItemListRail({
  task,
  isMultiSelectActive,
  selectedTaskIds,
  onToggleInSelection,
  localCompleted,
  setLocalCompleted,
  user,
  handleToggleComplete,
  activeFocusTaskId,
  focusStartTime,
  stopFocus,
  isSmallScreen,
  isExecutionView,
  hideCheckboxFromTabOrder,
  isTouchDevice,
  isFocused,
  showPlayButton,
  handleStartFocus,
  stopAllPropagation,
}) {
  const { topActionableTaskId = null } = useTaskUI();
  const hintQueue = useOnboardingHintQueueContext();
  const highlightFocusPlay =
    hintQueue?.activeId === ONBOARDING_HINT_FOCUS &&
    Boolean(topActionableTaskId) &&
    topActionableTaskId === task.id;

  return (
    <Box className="task-item__rail" data-testid="task-item__rail">
      {isMultiSelectActive ? (
        <Checkbox
          tabIndex={-1}
          checked={selectedTaskIds.includes(task.id)}
          onChange={(e) => {
            e.stopPropagation();
            onToggleInSelection(task.id);
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          size="small"
        />
      ) : (
        <TaskCheckbox
          task={task}
          checked={localCompleted}
          onToggle={(isChecked) => {
            setLocalCompleted(isChecked);
            if (user) {
              handleToggleComplete(task, isChecked, {
                activeFocusTaskId,
                focusStartTime,
                stopFocus,
              }).catch((err) =>
                console.error(
                  '[TaskItemBase] handleToggleComplete failed:',
                  err,
                ),
              );
            } else {
              console.warn(
                '[TaskItemBase] handleToggleComplete skipped because user is null',
              );
            }
          }}
          isSmallScreen={isSmallScreen}
          isExecutionView={isExecutionView}
          hideFromTabOrder={hideCheckboxFromTabOrder}
          isTouchDevice={isTouchDevice}
          onLongPress={() => onToggleInSelection(task.id)}
        />
      )}
      {isExecutionView ? (
        <TaskFocusButton
          task={task}
          isFocused={isFocused}
          showPlayButton={showPlayButton}
          user={user}
          handleStartFocus={handleStartFocus}
          stopFocus={stopFocus}
          stopAllPropagation={stopAllPropagation}
          highlight={highlightFocusPlay}
        />
      ) : null}
    </Box>
  );
}
